import type { TrackingEventName } from '@novacredit/frontend-common/tracking';
import type { ConsentMap } from '@novacredit/frontend-common/consent/common';
import type { Env } from '@novacredit/frontend-common/types';
import { ENV } from '@novacredit/frontend-common/utils/env';
import tracker from './tracking';

/**
 * A simplified version of the tracking client. We don't want to expose
 * all the nuances of the tracker to whoever needs to configure the tag
 * on a site that is not owned by Nova engineering.
 */
type NovaAnalytics = {
	track: (event: string, properties?: Object) => void;
	trackPage: (pageName?: string, properties?: Object) => void;
	onConsentUpdate: (consents: ConsentMap) => void;
	setUserProps: (properties: Object, options?: Object) => void;
	// Before this tracking script has the chance to run, calls can be pushed
	// onto a queue and later dispatched below.
	calls: (
		| ['track', string, Object]
		| ['trackPage', string, Object]
		| ['onConsentUpdate', ConsentMap]
		| ['setUserProps', Object, Object]
	)[];
	// set to `false` on the stub version and `true` on the real version.
	initialized: boolean;
	// set to 'development'/'staging'/'production' on the stubbed version.
	environment?: Env;
};

declare global {
	interface Window {
		NovaAnalytics: NovaAnalytics;
	}
}

/**
 * Ensure that the snippet which creates the stubbed version
 * of NovaAnalytics was properly configured.
 */
const validateStubbedNovaAnalytics = () => {
	const conditions = [
		!window.NovaAnalytics,
		!Array.isArray(window.NovaAnalytics?.calls),
		window.NovaAnalytics.initialized,
		!Object.keys(ENV).includes(window.NovaAnalytics?.environment),
	];

	if (conditions.some(Boolean)) {
		throw new Error('NovaAnalytics not configured correctly. Tracking has been disabled.');
	}
};

/**
 * Allow the tracking client to begin sending event payloads
 * to the eventing back-end.
 */
const initializeTrackingClient = (stubbedNovaAnalytics: NovaAnalytics) => {
	tracker.initialize({
		nodeEnv: stubbedNovaAnalytics.environment,
		// This could be enhanced in the future to support other userEnv's such as `sandbox`,
		// but leaving as `production` for now for simplicity.
		userEnv: 'production',
		options: {
			enableCookieConsent: true,
		},
	});
};

/**
 * Create and returns a `NovaAnalytics` object, a wrapper around the
 * TrackingClient with a simpler interface.
 */
const createNovaAnalytics = (): NovaAnalytics => ({
	track: (event: string, properties: Object = {}) => {
		const formalEventName = ['nova', 'marketing', event].join('.');
		tracker.track(formalEventName as TrackingEventName, properties, {
			skipEventValidation: true,
		});
	},
	trackPage: (pageName = '', properties: Object = {}) => {
		tracker.trackPage(window.location.href, { ...properties, pageName });
	},
	setUserProps: (properties: Object = {}, options: Object = {}) => {
		tracker.setUserProps(properties, options);
	},
	calls: [],
	onConsentUpdate: (consents: ConsentMap) => {
		tracker.cookieConsentUpdated(consents);
	},
	initialized: true,
});

/**
 * If any events fired while the stubbed NovaAnalytics was in place, and
 * before the real NovaAnalytics was made available, send those queued
 * events to the eventing back-end.
 */
const trackQueuedEvents = ({
	stubbedNovaAnalytics,
	novaAnalytics,
}: {
	stubbedNovaAnalytics: NovaAnalytics;
	novaAnalytics: NovaAnalytics;
}) => {
	stubbedNovaAnalytics.calls.forEach(args => {
		const method = args[0];

		if (method === 'track') {
			const event = args[1];
			const properties = args[2];
			novaAnalytics.track(event, properties);
		} else if (method === 'trackPage') {
			const pageName = args[1];
			const properties = args[2];
			novaAnalytics.trackPage(pageName, properties);
		} else if (method === 'onConsentUpdate') {
			const consent = args[1];
			novaAnalytics.onConsentUpdate(consent);
		} else if (method === 'setUserProps') {
			const properties = args[1];
			const options = args[2];
			novaAnalytics.setUserProps(properties, options);
		}
	});
};

/**
 * Makes a `NovaAnalytics` object available on the window for
 * simple page and custom event tracking.
 */
export const initializeNovaAnalytics = () => {
	validateStubbedNovaAnalytics();
	const stubbedNovaAnalytics = window.NovaAnalytics;
	initializeTrackingClient(stubbedNovaAnalytics);
	const novaAnalytics = createNovaAnalytics();
	trackQueuedEvents({ stubbedNovaAnalytics, novaAnalytics });
	window.NovaAnalytics = novaAnalytics;
};
